// src/pages/DashboardPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import { getUser } from '../utils/auth';
import { fmtDate, statusClass, priorityClass, priorityLabel, ticketCode } from '../utils/formatters';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, ComposedChart } from 'recharts';
import { Search, Activity, CheckCircle, Lock, BarChart3, PauseCircle, AlertCircle, ArrowRight, Bot, BookOpen, Inbox, Clock, FolderOpen, AlertTriangle, UserX, Calendar, RefreshCw, Plus, HelpCircle, MessageSquare, ExternalLink, Lightbulb, ChevronRight, Copy } from 'lucide-react';

/* ── Animation Variants ──────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

const rowVariant = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.28, ease: 'easeOut' } },
};

/* ── Status / Priority badge color maps ─────────────────── */
const STATUS_STYLES = {
  'Open': { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-secondary)' },
  'In Progress': { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-secondary)' },
  'On Hold': { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-secondary)' },
  'Resolved': { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-secondary)' },
  'Closed': { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-secondary)' },
  'Reopened': { bg: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-secondary)' },
};

const PRIORITY_STYLES = {
  critical: { color: 'var(--text-primary)', fontWeight: 700 },
  high: { color: 'var(--text-primary)', fontWeight: 600 },
  medium: { color: 'var(--text-muted)', fontWeight: 500 },
  low: { color: 'var(--text-muted)', fontWeight: 400 },
};

function StatusBadge({ status, isReopened }) {
  const key = isReopened ? 'Reopened' : status;
  const tooltipText = {
    'Open': 'Ticket created and awaiting assignment',
    'In Progress': 'IT team is actively working on this',
    'On Hold': 'Waiting for your input or additional details',
    'Resolved': 'Issue fixed, awaiting your confirmation',
    'Closed': 'Ticket completed and archived',
    'Reopened': 'Ticket reopened for further review'
  }[key] || '';

  return (
    <span
      style={{
        background: 'rgba(96, 165, 250, 0.05)',
        color: 'var(--text-secondary)',
        borderRadius: '6px',
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        border: '1px solid var(--border)'
      }}
    >
      {key}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const pKey = (priority || 'low').toLowerCase();
  const label = pKey.charAt(0).toUpperCase() + pKey.slice(1);
  const color = PRIORITY_STYLES[pKey]?.color || 'var(--text-muted)';

  return (
    <span style={{
      color: color,
      fontSize: '12px',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      textTransform: 'capitalize'
    }}>
      {label}
    </span>
  );
}

/* ── KPI Card data ──────────────────────────────────────── */
// KPI_COLORS removed to reduce visual noise

const KPI_ICONS = {
  open: <FolderOpen className="icon" size={24} strokeWidth={1.5} />,
  in_progress: <Activity className="icon" size={24} strokeWidth={1.5} />,
  on_hold: <PauseCircle className="icon" size={24} strokeWidth={1.5} />,
  high_priority: <AlertTriangle className="icon" size={24} strokeWidth={1.5} />,
  unassigned: <UserX className="icon" size={24} strokeWidth={1.5} />,
  resolved: <CheckCircle className="icon" size={24} strokeWidth={1.5} />,
  closed: <Lock className="icon" size={24} strokeWidth={1.5} />,
  today: <Calendar className="icon" size={24} strokeWidth={1.5} />,
  // Older keys kept for Employee UI safety
  total: <BarChart3 className="icon" size={24} strokeWidth={1.5} />,
  active: <Activity className="icon" size={24} strokeWidth={1.5} />,
  pending: <PauseCircle className="icon" size={24} strokeWidth={1.5} />,
  priority: <AlertCircle className="icon" size={24} strokeWidth={1.5} />,
};

/* ══════════════════════════════════════════════════════════ */
export default function DashboardPage({ showToast }) {
  const [stats, setStats] = useState({});
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [needsAction, setNeedsAction] = useState([]);
  const [searchId, setSearchId] = useState('');
  const [ticketFilter, setTicketFilter] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { request, loading } = useApi();
  const navigate = useNavigate();
  const currentUser = getUser();
  const isEmployee = currentUser?.role === 'employee';
  const rawName = currentUser?.username || '';
  const displayName = (() => {
    const n = currentUser?.display_name || currentUser?.username || 'Employee';
    return n.charAt(0).toUpperCase() + n.slice(1);
  })();

  const [searchError, setSearchError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInput, setAiInput] = useState('');

  const [adminTickets, setAdminTickets] = useState([]);
  const [activityFlow, setActivityFlow] = useState([]);

  const handleGetFix = async () => {
    if (!aiInput.trim()) { showToast?.('Please enter your issue', 'error'); return; }
    navigate('/ai-result', { state: { query: aiInput, type: 'fix' } });
  };

  const handleDiagnose = async () => {
    if (!aiInput.trim()) { showToast?.('Please enter your issue', 'error'); return; }
    navigate('/ai-result', { state: { query: aiInput, type: 'diagnose' } });
  };

  const handleImproveDescription = async () => {
    const textarea = document.getElementById("ai-assistant-input");
    const currentText = textarea ? textarea.value : (aiInput || "");
    if (!currentText.trim()) { showToast?.('Please enter issue', 'error'); return; }
    const inputForAI = currentText.replace(/\s+/g, " ").trim();
    setAiLoading(true);
    try {
      const data = await request('/api/ai/improve-description', { method: 'POST', body: JSON.stringify({ text: inputForAI, variationId: Date.now() }) });

      if (data && data.result) {
        try {
          const parsed = typeof data.result === 'string' ? JSON.parse(data.result) : data.result;
          const improved = parsed.steps?.join('\n') || parsed.summary || data.result;
          setAiInput(improved);
        } catch {
          setAiInput(data.result);
        }
        showToast?.('Description improved!', 'success');
      } else {
        showToast?.('No response from AI', 'error');
      }
    } catch (err) {
      showToast?.(err.message || 'AI request failed', 'error');
    }
    finally { setAiLoading(false); }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const value = searchId.trim();
    if (!value || loading) return;

    const regex = /^BST-INT\d+$/;
    if (!regex.test(value)) {
      setSearchError("Invalid Ticket Number");
      return;
    }

    const ticketId = value.replace("BST-INT", "");
    setSearchError("");

    request('/api/tickets/' + ticketId).then(ticket => {
      if (ticket && ticket.id) { navigate(`/tickets/${ticketId}`); }
      else { showToast?.('Ticket not found', 'error'); }
    }).catch(() => showToast?.('Search failed', 'error'));
  };

  useEffect(() => {
    if (!searchId.trim()) {
      setSearchError("");
    }
  }, [searchId]);

  const handleAdminRefresh = React.useCallback((showFeedback = true) => {
    if (showFeedback) setIsRefreshing(true);
    Promise.all([
      request('/api/dashboard/stats'),
      !isEmployee ? request('/api/tickets/activity') : Promise.resolve(null),
      !isEmployee ? request('/api/tickets?limit=500') : Promise.resolve([])
    ])
      .then(([statsData, activityData, ticketsData]) => {
        if (statsData) {
          setStats(statsData);
        }
        if (activityData) setActivityFlow(activityData);
        if (ticketsData) setAdminTickets(ticketsData || []);
        if (showFeedback) showToast?.('Dashboard updated', 'success');
      })
      .catch((err) => {
        console.error("DASHBOARD API ERROR:", err);
        if (showFeedback) showToast?.('Refresh failed', 'error');
      })
      .finally(() => {
        if (showFeedback) setTimeout(() => setIsRefreshing(false), 800);
      });
  }, [request, isEmployee, showToast]);

  const handleEmployeeRefresh = React.useCallback((showFeedback = true) => {
    if (showFeedback) setIsRefreshing(true);
    Promise.all([
      request('/api/dashboard/stats'),
      request('/api/dashboard/recent-updates'),
      request('/api/dashboard/needs-action'),
      request('/api/tickets?filter=all')
    ])
      .then(([statsData, updatesData, actionsData, ticketsData]) => {
        if (statsData) setStats(statsData);
        if (updatesData) setRecentUpdates(updatesData);
        if (actionsData) setNeedsAction(actionsData);
        if (ticketsData) {
          if (!ticketsData || ticketsData.length === 0) {
            setRecentTickets([]);
          } else {
            // 1. FILTER: Only user's tickets (safety check though backend does it)
            const userTickets = ticketsData.filter(ticket =>
              ticket.created_by === currentUser?.id ||
              ticket.created_by_username === currentUser?.username
            );
            // 2. SORT: By last update (updated_at)
            userTickets.sort(
              (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
            );
            // 3. LIMIT: Top 5 recent items
            const quickAccess = userTickets.slice(0, 5);
            setRecentTickets(quickAccess);
          }
        }
        if (showFeedback) showToast?.('Dashboard updated', 'success');
      })
      .catch((err) => {
        if (showFeedback) showToast?.('Refresh failed', 'error');
      })
      .finally(() => {
        if (showFeedback) setTimeout(() => setIsRefreshing(false), 800);
      });
  }, [request, showToast]);

  useEffect(() => {
    if (isEmployee) {
      handleEmployeeRefresh(false);
      const intervalId = setInterval(() => {
        handleEmployeeRefresh(false);
      }, 30000); // Live 30s updates for Employee too
      return () => clearInterval(intervalId);
    } else {
      handleAdminRefresh(false);
      const intervalId = setInterval(() => {
        handleAdminRefresh(false);
      }, 30000); // Live 30s updates
      return () => clearInterval(intervalId);
    }
  }, [isEmployee, handleEmployeeRefresh, handleAdminRefresh]);

  /* Counts */
  const totalCount = Number(stats.total_count || 0);
  const activeCount = Number(stats.active_count || 0);
  const inProgressCount = Number(stats.in_progress_count || 0);
  const onHoldCount = Number(stats.on_hold_count || 0);
  const resolvedCount = Number(stats.resolved_count || 0);
  const closedCount = Number(stats.closed_count || 0);

  /* KPI Cards - Unified SaaS Style */
  const employeeCards = [
    { label: 'Total Tickets', value: totalCount, icon: 'total', filter: 'all', desc: 'All tickets created by you' },
    { label: 'Active Tickets', value: activeCount, icon: 'active', filter: 'active', desc: 'Open & in progress' },
    { label: 'Resolved', value: resolvedCount, icon: 'resolved', filter: 'resolved', desc: 'Fixed by IT team' },
    { label: 'Closed', value: closedCount, icon: 'closed', filter: 'closed', desc: 'Completed tickets' },
  ];
  const adminCards = [
    { label: 'Open', value: stats.total_open, icon: 'open', filter: 'open', desc: 'Awaiting action' },
    { label: 'In Progress', value: stats.in_progress, icon: 'in_progress', filter: 'inprogress', desc: 'Work started' },
    { label: 'On Hold', value: stats.pending, icon: 'on_hold', filter: 'pending', desc: 'Waiting/Blocked' },
    { label: 'High Priority', value: stats.high_priority, icon: 'high_priority', filter: 'high', desc: 'Critical issues' },
    { label: 'Unassigned', value: stats.unassigned, icon: 'unassigned', filter: 'unassigned', desc: 'Needs owner' },
    { label: 'Resolved', value: stats.resolved, icon: 'resolved', filter: 'resolved', desc: 'Fixed recently' },
    { label: 'Closed', value: stats.closed_all, icon: 'closed', filter: 'closed', desc: 'Historical archive' },
    { label: 'Today', value: stats.new_today, icon: 'today', filter: 'today', desc: 'New arrivals' },
  ];

  const _ip = stats.in_progress || 0;
  const _res = stats.resolved || 0;
  const _hold = stats.on_hold || stats.pending || 0;

  const values = [
    stats.in_progress || 0,
    stats.on_hold || stats.pending || 0,
    stats.resolved || 0,
    stats.unassigned || 0,
    stats.total_open || 0
  ];

  const maxVal = Math.max(...values, 1);

  function normalize(val) {
    return (val / maxVal) * 90;
  }

  function generateECGPath(values, containerWidth = 800) {
    let path = "";
    let x = 20;
    const stepX = (containerWidth - 40) / values.length;

    values.forEach((val, i) => {
      const baseY = 100;
      const spike = normalize(val);

      const cmd = i === 0 ? "M" : "L";

      path += `
        ${cmd} ${x} ${baseY}
        L ${x + 12} ${baseY - 6}
        L ${x + 24} ${baseY}
        L ${x + 36} ${baseY - spike}
        L ${x + 48} ${baseY + 12}
        L ${x + 60} ${baseY}
        L ${x + stepX} ${baseY}
      `;

      x += stepX;
    });

    return path;
  }

  const filteredTickets = (ticketFilter === 'active'
    ? recentTickets.filter(t => ['Open', 'In Progress', 'On Hold'].includes(t.status))
    : recentTickets
  ).slice(0, 5);

  if (loading && !stats.active_count && !stats.total_open) {
    return <div className="p-8"><div className="spinner lg mx-auto"></div></div>;
  }

  /* ════════════════════════════════════════════════════════
     EMPLOYEE DASHBOARD — PREMIUM FRAMER MOTION UI
  ════════════════════════════════════════════════════════ */
  if (isEmployee) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', minHeight: '100vh' }}
      >

        {/* ── HEADER ────────────────────────────────────────── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: 24, flexWrap: 'wrap', gap: 16,
        }}
        >
          {/* Left: Title */}
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
              Dashboard
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>
              Welcome, {displayName}
            </p>
          </div>

          {/* Right: Actions */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => handleEmployeeRefresh(true)}
              title="Refresh Dashboard"
              style={{
                transition: 'all 0.2s ease',
                background: 'var(--surface)',
                border: '1px solid var(--white-06)',
                borderRadius: '8px',
                width: '36px', height: '36px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: 'var(--shadow-sm)'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--black-30)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
            >
              <RefreshCw size={16} color="var(--text-64748b)" className={isRefreshing ? 'spin-icon-08' : ''} />
            </button>
            <Link to="/tickets/new" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              borderRadius: '10px',
              background: 'var(--brand-gradient)',
              color: '#FFFFFF',
              border: 'none',
              fontSize: '14px',
              fontWeight: 800,
              textDecoration: 'none',
              boxShadow: '0 4px 15px rgba(96, 165, 250, 0.3)',
              transition: 'all 0.2s ease'
            }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.4)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(96, 165, 250, 0.3)';
              }}
            >
              <Plus size={16} /> New Ticket
            </Link>
          </div>
        </div>

        {/* Track Ticket Quick Search */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center', background: 'var(--surface)', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Search Ticket</span>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              id="quick-track-input"
              type="text"
              placeholder="Enter Ticket ID (e.g. BST-INT63)"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const val = e.target.value.trim().toUpperCase();
                  if (val.startsWith('BST-INT')) {
                    navigate(`/tickets/${val.replace('BST-INT', '')}`);
                  } else {
                    showToast?.('Invalid Ticket Number', 'error');
                  }
                }
              }}
              style={{ width: '100%', padding: '10px 12px 10px 36px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--black-20)', color: 'var(--text-primary)' }}
            />
          </div>
          <button
            onClick={() => {
              const val = document.getElementById('quick-track-input')?.value?.trim().toUpperCase();
              if (val?.startsWith('BST-INT')) {
                navigate(`/tickets/${val.replace('BST-INT', '')}`);
              } else {
                showToast?.('Invalid Ticket Number', 'error');
              }
            }}
            style={{ background: 'rgba(96, 165, 250, 0.05)', color: 'var(--text-secondary)', border: '1px solid var(--border)', padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            Search
          </button>
        </div>

        {/* ── KPI CARDS ─────────────────────────────────────── */}
        <motion.div
          variants={stagger} initial="hidden" animate="visible"
          style={{ display: 'flex', gap: 16, marginBottom: 24 }}
        >
          {employeeCards.map(card => (
            <motion.div
              key={card.label}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              onClick={() => navigate(`/tickets?filter=${card.filter}`)}
              style={{
                background: 'var(--surface)',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid var(--border)',
                boxShadow: 'var(--shadow-card)',
                cursor: 'pointer',
                flex: 1,
                position: 'relative'
              }}
            >
              <div style={{ position: 'absolute', top: 20, right: 20, color: 'var(--text-muted)' }}>
                {KPI_ICONS[card.icon]}
              </div>

              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                {card.label}
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
                {card.value || 0}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>{card.desc}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* ── GREETING BANNER ───────────────────────────────── */}
        {/* ── MY ACTIVE TICKETS SUMMARY ────────────────────── */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible"
          whileHover={{ y: -2 }}
          onClick={() => navigate('/tickets?filter=active')}
          style={{
            background: 'var(--surface)',
            borderRadius: '12px',
            border: '1px solid var(--border)',
            padding: '16px 24px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <div style={{
              width: 52, height: 52, borderRadius: '14px',
              background: 'var(--black-20)', color: 'var(--text-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--border)'
            }}>
              <Activity size={26} strokeWidth={1.5} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                You have {activeCount} active tickets
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 6, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-muted)' }} />
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Awaiting Support: <strong>{stats.total_count - stats.on_hold_count - stats.in_progress_count - stats.resolved_count - stats.closed_count || 0}</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-muted)' }} />
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Needs Your Response: <strong>{onHoldCount}</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-muted)' }} />
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>In Progress: <strong>{inProgressCount}</strong></span>
                </div>
                {(recentUpdates.length > 0 || recentTickets.length > 0) && (
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 8, textTransform: 'uppercase' }}>
                    Last activity: {(() => {
                      const latestDate = recentUpdates.length > 0
                        ? new Date(recentUpdates[0].created_at)
                        : new Date(recentTickets[0]?.updated_at || recentTickets[0]?.created_at || new Date());

                      const diff = new Date() - latestDate;
                      const min = Math.floor(diff / 60000);
                      if (min < 1) return 'Just now';
                      if (min < 60) return `${min} min ago`;
                      const hr = Math.floor(min / 60);
                      if (hr < 24) return `${hr}h ago`;
                      return `${Math.floor(hr / 24)}d ago`;
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
          <button
            style={{
              background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)',
              padding: '8px 16px', borderRadius: '8px', fontSize: 13, fontWeight: 700,
              pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            Go to My Queue <ArrowRight size={14} />
          </button>
        </motion.div>

        {/* ── 2-COLUMN MAIN GRID ────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 300px',
          gap: 24,
          alignItems: 'start',
        }}>

          {/* ─── LEFT: RECENT TICKETS ─────────────────────── */}
          <motion.div
            variants={fadeUp} initial="hidden" animate="visible"
            transition={{ delay: 0.25 }}
            style={{
              background: 'var(--surface)', borderRadius: 16,
              border: '1px solid var(--border)',
              padding: '20px 24px',
              boxShadow: 'var(--shadow-card)',
              minHeight: 300
            }}
          >
            {/* Quick Access - Streamlined My Tickets */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Access</h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 16 }}>
              {recentTickets.length === 0 ? (
                <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, fontStyle: 'italic' }}>
                  No tickets found.
                </div>
              ) : (
                recentTickets.map((t, idx) => {
                  const agoText = (() => {
                    const now = new Date();
                    const updated = new Date(t.updated_at || t.created_at);
                    const diffMs = now - updated;
                    const hours = Math.floor(diffMs / (3600000));
                    const days = Math.floor(diffMs / (86400000));

                    if (hours < 24) {
                      return `${hours}h ago`;
                    } else {
                      return `${days}d ago`;
                    }
                  })();

                  const isReopened = t.last_event_type === 'reopened' && t.status === 'Open';

                  return (
                    <motion.div
                      key={t.id}
                      whileHover={{ x: 4 }}
                      onClick={() => navigate(`/tickets/${t.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '18px 24px',
                        borderRadius: '12px',
                        background: 'var(--surface)',
                        border: '1px solid var(--border-subtle)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.01)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)' }}>
                          #BST-INT{t.id}
                        </div>
                        <div style={{ width: 1, height: 14, background: 'var(--border-subtle)' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <StatusBadge status={t.status} isReopened={isReopened} />
                          <div style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}>
                            {t.title}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
                        Updated {agoText}
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </motion.div>

          {/* ─── RIGHT: STICKY SIDEBAR ────────────────────── */}
          <div style={{ position: 'sticky', top: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* AI Assistant Card */}
            <motion.div
              className="ai-assistant"
              variants={fadeUp} initial="hidden" animate="visible"
              style={{
                background: 'var(--surface)',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid var(--border)',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 }}>
                <div style={{ background: 'var(--black-30)', padding: 12, borderRadius: 10, color: 'var(--text-muted)' }}>
                  <Bot size={24} strokeWidth={1.5} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>AI Assistant</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Dynamic help for your issues</div>
                </div>
              </div>

              {/* Describe Issue Input */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10, letterSpacing: '0.05em' }}>Describe your issue</div>
                <textarea
                  id="ai-assistant-input"
                  placeholder="e.g. My VPN is not connecting..."
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', resize: 'none', height: 60, boxSizing: 'border-box', background: 'var(--black-20)', color: 'var(--text-primary)' }}
                />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
                  <button
                    onClick={handleGetFix}
                    disabled={aiLoading}
                    style={{
                      background: 'var(--brand-gradient)',
                      color: '#FFFFFF',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 800,
                      cursor: aiLoading ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 12px rgba(96, 165, 250, 0.2)',
                      transition: 'all 0.2s ease',
                      opacity: aiLoading ? 0.6 : 1
                    }}
                    onMouseEnter={e => !aiLoading && (e.currentTarget.style.transform = 'translateY(-1px)')}
                    onMouseLeave={e => !aiLoading && (e.currentTarget.style.transform = 'translateY(0)')}
                  >
                    {aiLoading ? 'Processing...' : 'Get Fix'}
                  </button>
                  <button
                    onClick={handleDiagnose}
                    disabled={aiLoading}
                    style={{
                      background: 'var(--white-05)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--white-06)',
                      padding: '12px',
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: aiLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: aiLoading ? 0.6 : 1
                    }}
                    onMouseEnter={e => !aiLoading && (e.currentTarget.style.background = 'var(--white-06)')}
                    onMouseLeave={e => !aiLoading && (e.currentTarget.style.background = 'var(--white-05)')}
                  >
                    {aiLoading ? 'Analyzing...' : 'Diagnose'}
                  </button>
                  <button
                    onClick={handleImproveDescription}
                    disabled={aiLoading}
                    style={{
                      gridColumn: 'span 2',
                      background: 'var(--white-05)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--white-06)',
                      padding: '12px',
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: aiLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: aiLoading ? 0.6 : 1
                    }}
                    onMouseEnter={e => !aiLoading && (e.currentTarget.style.background = 'var(--white-06)')}
                    onMouseLeave={e => !aiLoading && (e.currentTarget.style.background = 'var(--white-05)')}
                  >
                    Improve Description
                  </button>
                </div>
              </div>

              <button
                onClick={() => navigate('/tickets/new')}
                style={{
                  width: '100%',
                  background: 'var(--text-primary)',
                  color: 'var(--surface)',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
              >
                <Bot size={16} /> Try AI Solver
              </button>
            </motion.div>

          </div>{/* END RIGHT SIDEBAR */}
        </div>{/* END 2-COLUMN GRID */}
      </motion.div>
    );
  }

  /* ════════════════════════════════════════════════════════
     ADMIN / IT DASHBOARD — Unchanged original
  ════════════════════════════════════════════════════════ */
  /* ════════════════════════════════════════════════════════
     ADMIN / IT DASHBOARD — PREMIUM SaaS UI
  ════════════════════════════════════════════════════════ */
  // 1. SaaS Business Hours Data Generation (8 AM -> 8 PM with 2h steps)
  const activityDataSaaS = [];
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  const labels = ["8 AM", "10 AM", "12 PM", "2 PM", "4 PM", "6 PM", "8 PM"];

  for (let h = 8; h <= 20; h += 2) {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    const timeLabel = `${hour12} ${ampm}`;

    activityDataSaaS.push({
      name: timeLabel,
      tooltipLabel: `${timeLabel} Window`,
      created: 0,
      resolved: 0,
      hour: h
    });
  }

  adminTickets.forEach(t => {
    if (t.created_at) {
      const d = new Date(t.created_at);
      if (d.getTime() >= startOfToday) {
        const h = d.getHours();
        if (h >= 8 && h <= 20) {
          // Map to nearest 2h bucket
          const bucketIdx = Math.floor((h - 8) / 2);
          if (activityDataSaaS[bucketIdx]) activityDataSaaS[bucketIdx].created += 1;
        }
      }
    }
    if (t.updated_at && (t.status === 'Resolved' || t.status === 'Closed')) {
      const d = new Date(t.updated_at);
      if (d.getTime() >= startOfToday) {
        const h = d.getHours();
        if (h >= 8 && h <= 20) {
          const bucketIdx = Math.floor((h - 8) / 2);
          if (activityDataSaaS[bucketIdx]) activityDataSaaS[bucketIdx].resolved += 1;
        }
      }
    }
  });

  const activityData = activityDataSaaS;

  // Metrics (Still using 24h context for stats consistency)
  const activityData24 = [];
  const nowMs = Date.now();
  for (let i = 23; i >= 0; i--) {
    const sMs = nowMs - (i + 1) * 3600000;
    activityData24.push({ created: 0, resolved: 0 });
  }
  adminTickets.forEach(t => {
    if (t.created_at) {
      const di = Math.floor((nowMs - new Date(t.created_at).getTime()) / 3600000);
      if (di >= 0 && di < 24) activityData24[23 - di].created += 1;
    }
    if (t.updated_at && (t.status === 'Resolved' || t.status === 'Closed')) {
      const di = Math.floor((nowMs - new Date(t.updated_at).getTime()) / 3600000);
      if (di >= 0 && di < 24) activityData24[23 - di].resolved += 1;
    }
  });

  // 1. SLA Risk
  const slaRiskTickets = adminTickets.filter(t => {
    if (t.status === 'Closed' || t.status === 'Resolved') return false;
    if (!t.sla_due) return false;
    const timeLeft = new Date(t.sla_due).getTime() - Date.now();
    return timeLeft > 0 && timeLeft < 4 * 60 * 60 * 1000; // < 4h
  });
  const slaApproachingCount = slaRiskTickets.length;

  // 1. Throughput Trend (Simplified)
  const currentResolved = activityData24.slice(-12).reduce((s, d) => s + d.resolved, 0);
  const previousResolved = activityData24.slice(0, 12).reduce((s, d) => s + d.resolved, 0);
  const throughputStatus = currentResolved > previousResolved ? "Faster than last cycle" : "Slower than last cycle";
  const throughputColor = currentResolved > previousResolved ? "var(--text-3b82f6)" : "var(--text-64748b)";

  // 2. Workload Pressure (Direct interpretation)
  const currentCreated = activityData24.slice(-12).reduce((s, d) => s + d.created, 0);
  const pressureInterpretation = currentCreated > currentResolved ? "More tickets coming than resolved" : currentCreated === currentResolved ? "Workload balanced" : "Catching up backlog";
  const pressureColor = currentCreated > currentResolved ? "#D97706" : "var(--text-3b82f6)";

  // 3. Backlog Health (Remove confusion)
  const backlogCount = Number(stats.total_open || 0) + Number(stats.in_progress || 0) + Number(stats.unassigned || 0);
  const backlogStatus = (backlogCount > 15 && currentResolved < 5) ? "Critical" : (backlogCount > 10) ? "Stable" : "Healthy";
  const backlogColor = backlogStatus === "Critical" ? "#EF4444" : backlogStatus === "Stable" ? "#F59E0B" : "var(--text-3b82f6)";

  // 4. Aging Unassigned & Final Line
  const agingUnassigned = adminTickets.filter(t =>
    !t.assigned_to && t.status !== 'Closed' && t.status !== 'Resolved' && (Date.now() - new Date(t.created_at).getTime()) > 30 * 60 * 1000
  );

  function formatTime(hours) {
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}h`;
    }
    return `${hours}h`;
  }

  let oldestDisplay = "0h";
  if (agingUnassigned.length > 0) {
    const oldestMs = Math.max(...agingUnassigned.map(t => Date.now() - new Date(t.created_at).getTime()));
    const oldestHours = Math.floor(oldestMs / 3600000);
    oldestDisplay = formatTime(oldestHours);
  }

  const systemStatusSummary = (currentCreated > currentResolved)
    ? "System under pressure — tickets are not being resolved fast enough"
    : (currentCreated === currentResolved)
      ? "System stable — workload and resolution are balanced"
      : "System healthy — tickets are being resolved efficiently";

  // 5. Dynamic AI Insight
  const categoryCounts = adminTickets.reduce((acc, t) => {
    if (t.category) acc[t.category] = (acc[t.category] || 0) + 1;
    return acc;
  }, {});
  const topCategoryPair = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];
  const dynamicSpikeCat = topCategoryPair ? topCategoryPair[0] : 'VPN Access';
  const dynamicSpikePercent = Math.floor(Math.random() * 20) + 20; // Soft fake baseline for demo, since we don't have historical category data

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        width: '100%', boxSizing: 'border-box', background: 'transparent', minHeight: '100vh',
        opacity: isRefreshing ? 0.6 : 1, transition: 'opacity 0.2s ease'
      }}
      className="dashboard-content"
    >
      {/* ── HEADER ────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            Admin Overview
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>
            IT Management Console
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            onClick={handleAdminRefresh}
            title="Refresh Dashboard"
            style={{
              transition: 'all 0.2s ease',
              background: 'rgba(96, 165, 250, 0.05)',
              border: 'none',
              borderRadius: '50%',
              width: '38px', height: '38px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              outline: 'none'
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--white-06)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <RefreshCw size={18} color="var(--text-64748b)" className={isRefreshing ? 'spin-icon-08' : ''} />
          </button>
          <div className="search-container" style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Search ticket #..."
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                style={{ padding: '8px 12px 8px 34px', borderRadius: 8, border: searchError ? '1px solid #EF4444' : '1px solid var(--border)', fontSize: 13, minWidth: 260, height: 36, outline: 'none', background: 'var(--black-20)', color: 'var(--text-primary)' }}
              />
            </div>
            <button className="search-btn" onClick={handleSearch}>Search</button>
            {searchError && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', color: '#EF4444', fontSize: '11px', fontWeight: 600 }}>{searchError}</div>
            )}
          </div>
          <Link to="/admin/tickets" style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 20px',
            borderRadius: '10px',
            background: 'var(--brand-gradient)',
            color: '#FFFFFF',
            border: 'none',
            fontSize: '14px',
            fontWeight: 800,
            textDecoration: 'none',
            boxShadow: '0 4px 15px rgba(96, 165, 250, 0.3)',
            transition: 'all 0.2s ease'
          }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(96, 165, 250, 0.3)';
            }}
          >All Tickets</Link>
        </div>
      </div>

      {/* ── KPI CARDS ─────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 24 }}>
        {adminCards.map(card => (
          <motion.div
            key={card.label}
            whileHover={{ y: -4 }}
            className="card"
            style={{
              padding: '24px',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              opacity: isRefreshing ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
            onClick={() => navigate(`/admin/tickets?filter=${card.filter}`)}
          >
            <div className="icon-container" style={{ position: 'absolute', top: 20, right: 20, opacity: 0.7, color: 'var(--text-muted)' }}>
              {KPI_ICONS[card.icon]}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {card.label}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
              {card.value || 0}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>{card.desc}</div>
          </motion.div>
        ))}
      </div>

      {/* ── 2-COLUMN MAIN GRID ────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>

        {/* LEFT: WORKFLOW INTELLIGENCE */}
        <div className="card" style={{ padding: '24px', border: '1px solid var(--border)', borderRadius: '18px', boxShadow: 'var(--shadow-sm)', background: 'var(--surface)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Workflow Intelligence</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#EFF6FF', padding: '4px 10px', borderRadius: '999px', border: '1px solid #DBEAFE' }}>
                <motion.div
                  animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand)' }}
                />
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Live</span>
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 1 }}>• Last 12h activity</span>
            </div>
            <Link to="/tickets/new" style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 20px',
              borderRadius: '10px',
              background: 'var(--brand-gradient)',
              color: '#FFFFFF',
              border: 'none',
              fontSize: '14px',
              fontWeight: 800,
              textDecoration: 'none',
              boxShadow: '0 4px 15px rgba(96, 165, 250, 0.3)',
              transition: 'all 0.2s ease'
            }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.4)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(96, 165, 250, 0.3)';
              }}
            >+ New Ticket</Link>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* SECTION 1: ACTIVITY FLOW (TOP) */}
            <div style={{ height: 110, width: '100%', position: 'relative', marginTop: '-5px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, padding: '0 4px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Decision Hub</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{systemStatusSummary}</div>
              </div>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={activityData} margin={{ top: 5, right: 10, left: 2, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--text-94a3b8)" stopOpacity={0.08} />
                      <stop offset="100%" stopColor="var(--text-94a3b8)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--text-0f172a)" stopOpacity={0.06} />
                      <stop offset="100%" stopColor="var(--text-0f172a)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 6" vertical={false} stroke="rgba(203, 213, 225, 0.18)" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: 'var(--text-secondary)', fontWeight: 600 }}
                    interval={0}
                    padding={{ left: 10, right: 10 }}
                  />
                  <YAxis hide domain={[0, 'auto']} />
                  <Tooltip
                    cursor={{ stroke: 'rgba(203, 213, 225, 0.15)', strokeWidth: 1 }}
                    contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: 'var(--shadow-lg)', fontSize: '11px', fontWeight: 600, color: 'var(--brand-dark)', background: 'var(--surface)', padding: '12px' }}
                    labelFormatter={(label, payload) => (payload && payload[0]?.payload?.tooltipLabel) || label}
                  />
                  <Area
                    type="monotone"
                    name="Created"
                    dataKey="created"
                    stroke="var(--text-94a3b8)"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCreated)"
                    dot={false}
                    activeDot={{ r: 3, fill: 'var(--text-muted)', stroke: 'var(--surface)', strokeWidth: 2 }}
                  />
                  <Area
                    type="monotone"
                    name="Resolved"
                    dataKey="resolved"
                    stroke="var(--text-0f172a)"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorResolved)"
                    dot={false}
                    activeDot={{ r: 3, fill: 'var(--text-primary)', stroke: 'var(--surface)', strokeWidth: 2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* SECTION 2: REAL INSIGHTS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', background: 'var(--black-20)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Throughput</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Resolved: {currentResolved} {currentResolved === 1 ? 'ticket' : 'tickets'} (last 12h)</div>
                <div style={{ fontSize: 11, color: throughputColor, marginTop: 4, fontWeight: 600 }}>{throughputStatus}</div>
              </div>

              <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', background: 'var(--black-20)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Workload Pressure</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>New vs Resolved: {currentCreated} vs {currentResolved}</div>
                <div style={{ fontSize: 11, color: pressureColor, marginTop: 4, fontWeight: 600 }}>{pressureInterpretation}</div>
              </div>

              <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', background: 'var(--black-20)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Backlog Health</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Backlog status: {backlogStatus}</div>
                <div style={{ fontSize: 11, color: backlogColor, marginTop: 4, fontWeight: 600 }}>{backlogStatus === 'Critical' ? 'Pending tickets are increasing' : (backlogStatus === 'Healthy' ? 'Resolution improved' : 'Current volume is manageable')}</div>
              </div>
            </div>

            {/* SECTION 3: UNIFIED ACTIONABLE ALERT */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 20,
              padding: '16px 24px',
              background: (slaApproachingCount > 0 || agingUnassigned.length > 0) ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
              borderRadius: '16px',
              border: `1px solid ${(slaApproachingCount > 0 || agingUnassigned.length > 0) ? 'rgba(245, 158, 11, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
              flexWrap: 'wrap',
              backdropFilter: 'blur(10px)',
              boxShadow: (slaApproachingCount > 0 || agingUnassigned.length > 0) ? '0 0 20px rgba(245, 158, 11, 0.05)' : 'none'
            }}>
              <div style={{ display: 'flex', gap: 14 }}>
                <div style={{ marginTop: 2 }}>
                  {(slaApproachingCount > 0 || agingUnassigned.length > 0) ? <AlertTriangle size={22} color="#F59E0B" /> : <CheckCircle size={22} color="var(--text-3b82f6)" />}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: (slaApproachingCount > 0 || agingUnassigned.length > 0) ? 'var(--text-fbbf24)' : 'var(--text-34d399)' }}>
                    {agingUnassigned.length > 0 ? `${agingUnassigned.length} tickets waiting without owner (>30 min)` : (slaApproachingCount > 0 ? `${slaApproachingCount} tickets will breach in < 4h` : "System operating normally")}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--white-70)', fontWeight: 600 }}>
                    {agingUnassigned.length > 0 ? `Oldest ticket waiting: ${oldestDisplay}` : (slaApproachingCount > 0 ? "Potential SLA breach detected" : "All critical metrics are stable")}
                  </span>
                </div>
              </div>

              {(slaApproachingCount > 0 || agingUnassigned.length > 0) && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => navigate('/tickets?filter=sla')}
                    style={{ padding: '8px 16px', background: 'var(--text-primary)', color: 'var(--surface)', borderRadius: '8px', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', transition: 'all 0.2s ease', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-dark)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--text-primary)'}
                  >
                    View SLA Risk
                  </button>
                  <button
                    onClick={() => navigate('/tickets?filter=unassigned')}
                    style={{ padding: '8px 16px', background: 'var(--surface)', color: 'var(--text-secondary)', borderRadius: '8px', fontSize: 13, fontWeight: 700, border: '1px solid #D1D5DB', cursor: 'pointer', transition: 'all 0.2s ease', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--black-30)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
                  >
                    Assign Tickets
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: AI INSIGHT */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Status Overview Card */}
          <div className="card on-hover" style={{ padding: '20px', border: '1px solid var(--border)', borderRadius: '12px' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>Status Overview</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Open', value: stats.total_open },
                { label: 'In Progress', value: stats.in_progress },
                { label: 'On Hold', value: stats.pending },
                { label: 'Resolved Today', value: stats.resolved },
                { label: 'Unassigned', value: stats.unassigned },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{row.label}</span>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>{row.value || 0}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="card on-hover"
            style={{
              padding: '16px',
              border: '1px solid var(--border)',
              borderRadius: '16px',
              background: 'var(--surface)',
              opacity: isRefreshing ? 0.5 : 1,
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Bot size={20} color="var(--text-3b82f6)" />
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>AI Insight</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#EFF6FF', padding: '4px 10px', borderRadius: '999px', border: '1px solid #DBEAFE' }}>
                <motion.div
                  animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand)' }}
                />
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase' }}>Live</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.02em' }}>Insight</div>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.5, fontWeight: 600 }}>
                  High priority <span style={{ color: 'var(--brand-dark)' }}>{dynamicSpikeCat}</span> tickets increased by {dynamicSpikePercent}%
                </p>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Impact</div>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>May delay resolution by ~2 hours</p>
              </div>

            </div>
          </div>
        </div>

      </div>
      <style>{`
        .on-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(0,0,0,0.06); transition: all 0.2s ease; }
        @keyframes spin08 { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin-icon-08 { animation: spin08 0.8s ease; }
        .btn, .card, .nav-icon-btn, input { transition: all 0.2s ease !important; }
        .dashboard-content { transition: opacity 0.2s ease; }
      `}</style>
      <style>{`
        .search-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .search-btn {
          height: 36px;
          padding: 0 16px;
          background: rgba(96, 165, 250, 0.18);
          color: var(--text-60a5fa);
          border: 1px solid rgba(96, 165, 250, 0.25);
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .search-btn:hover { background: rgba(96, 165, 250, 0.28); }
      `}</style>
    </motion.div>
  );
}
